import type { ReactElement } from 'react';
import { Box, Button, Card, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import AlarmOutlined from '@mui/icons-material/AlarmOutlined';
import ChatBubbleOutlineOutlined from '@mui/icons-material/ChatBubbleOutlineOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import { StatusChip } from '../../components/StatusChip';
import { toneColor, type Tone } from '../../components/tone';
import { markRead } from './readStore';
import type { Notif, NotifCategory } from './build';

const CATEGORY: Record<NotifCategory, { icon: ReactElement; tone: Tone }> = {
  Approvals: { icon: <DescriptionOutlined />, tone: 'warning' },
  'Shipment Updates': { icon: <LocalShippingOutlined />, tone: 'info' },
  'Financial Alerts': { icon: <ReceiptLongOutlined />, tone: 'warning' },
  Reminders: { icon: <AlarmOutlined />, tone: 'error' },
  'Claude Errors': { icon: <ChatBubbleOutlineOutlined />, tone: 'error' },
  'Routine Updates': { icon: <SettingsOutlined />, tone: 'neutral' },
};

/** One notification: what it is, who made it and when, how urgent, and the button that takes you to it */
export function NotificationRow({ n, read }: { n: Notif; read: boolean }) {
  const { icon, tone } = CATEGORY[n.category];
  return (
    <Card sx={{ opacity: read ? 0.7 : 1 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ p: 1.5, alignItems: { sm: 'center' } }}>
        <Stack direction="row" spacing={1.5} sx={{ flex: 1, minWidth: 0, alignItems: 'center' }}>
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: 44,
              height: 44,
              borderRadius: 2,
              flexShrink: 0,
              color: toneColor(tone),
              bgcolor: `color-mix(in srgb, ${toneColor(tone)} 14%, transparent)`,
            }}
          >
            {icon}
          </Box>
          <Typography sx={{ fontWeight: 700, width: 74, flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
            {n.srb || '—'}
          </Typography>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              {!read && (
                <Box
                  aria-label="Unread"
                  sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }}
                />
              )}
              <Typography sx={{ fontWeight: read ? 600 : 750 }}>
                {n.srb && (
                  <Box component="span" sx={{ display: { md: 'none' }, color: 'text.secondary', mr: 0.75 }}>
                    {n.srb}
                  </Box>
                )}
                {n.title}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {n.text}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {n.by} · {n.when}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
          <StatusChip label={n.category} tone={tone} />
          <StatusChip label={n.priority} tone={n.priority === 'High' ? 'error' : 'warning'} />
          <Button
            component={RouterLink}
            to={n.action.to}
            variant={n.group === 'Needs Attention' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => markRead([n.id])}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {n.action.label}
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
}
